// Visual size picker for the build_car_kit "size" axis. Renders one
// tile per chip with the merchant-supplied size image (Small/Medium/
// Large/XL). Falls back to an inline SVG silhouette if the CDN image
// fails to load, so the picker still works if a file is missing or the
// chip label doesn't match a known size.

import { useState, type ReactElement } from "react"
import { useMediaQuery } from "../hooks/useMediaQuery"

interface SizeChip {
  value: string
  label: string
  helperText?: string
}

interface Props {
  chips: SizeChip[]
  priceDelta?: Record<string, number>
  onSelect: (chipLabel: string) => void
}

// Merchant-curated size images hosted on Shopify CDN. Map keyed by a
// normalized form of the chip label so we tolerate "Extra Large" vs
// "XL" vs "extra-large" naming.
const SIZE_IMAGE_URLS: Record<string, string> = {
  small:
    "https://cdn.shopify.com/s/files/1/0973/6008/files/Small_c7104a7d-5e4f-4bb8-ac70-2f210fc59c04.webp",
  medium:
    "https://cdn.shopify.com/s/files/1/0973/6008/files/Medium_e760f540-464c-4d6a-a5ba-7538f99f182b.webp",
  large:
    "https://cdn.shopify.com/s/files/1/0973/6008/files/Large_c1e4f685-cc88-4416-8358-6577f128a1a1.webp",
  xl:
    "https://cdn.shopify.com/s/files/1/0973/6008/files/XL_8e03ddfd-ab85-4941-aa6d-8209b91e8b9b.webp",
  "extra large":
    "https://cdn.shopify.com/s/files/1/0973/6008/files/XL_8e03ddfd-ab85-4941-aa6d-8209b91e8b9b.webp",
}

function imageUrlFor(label: string, value: string): string | null {
  const labelKey = label.trim().toLowerCase()
  const valueKey = value.trim().toLowerCase()
  return SIZE_IMAGE_URLS[labelKey] ?? SIZE_IMAGE_URLS[valueKey] ?? null
}

export function SizeSilhouetteGallery({ chips, priceDelta, onSelect }: Props) {
  // Deterministic columns instead of auto-fit: the auto-fit minmax left the
  // 4th size ("Extra Large") wrapping to a 2nd row on the narrower desktop
  // inline (canvas-off) bubble. Mobile = 2 columns (2×2), desktop = one row
  // (all sizes side by side).
  const isMobile = useMediaQuery("(max-width: 767px)")
  const cols = isMobile ? 2 : Math.min(chips.length, 4)
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          opacity: 0.6,
          marginBottom: 10,
          textAlign: "center",
        }}
      >
        Tap a size to pick
      </div>
      <div
        style={{
          display: "grid",
          // Responsive grid: 2-up on mobile, 4-up on desktop canvas.
          // The 140px threshold was too tight for the canvas-OFF inline
          // flow — Pearl's bubble is `max-width: 86%`, so on a 393px
          // viewport the gallery slot is ~290px, and 2*140+8gap=288px
          // sat on the knife edge. Dropped to 110px so it always reflows
          // to 2-up at typical mobile widths (even 320px) and still
          // gives 4-up at ≥460px (desktop canvas pane is ~600px).
          // Caught 2026-06-03 — first responsive pass kept some tiles
          // collapsing to 1-up depending on which container they landed in.
          // 2026-06-05: switched from auto-fit to a fixed column count so XL
          // never orphans to a 2nd row on desktop.
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 8,
        }}
      >
      {chips.map((c) => {
        const imageUrl = imageUrlFor(c.label, c.value)
        const delta = priceDelta?.[c.value]
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onSelect(c.label)}
            title={c.helperText}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 6,
              padding: "8px 7px 7px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "inherit",
              cursor: "pointer",
              font: "inherit",
              textAlign: "left",
              transition:
                "background 120ms ease, border-color 120ms ease, transform 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(109, 195, 247, 0.12)"
              e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.55)"
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,195,247,0.18)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)"
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"
              e.currentTarget.style.boxShadow = "none"
            }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                background:
                  "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)",
                borderRadius: 9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 4,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                overflow: "hidden",
              }}
            >
              <SizeImageOrFallback imageUrl={imageUrl} label={c.label} value={c.value} />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 4,
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.label}
              </span>
              {delta != null && (
                <span style={{ opacity: 0.7, fontSize: 10.5, flexShrink: 0 }}>
                  +${(delta / 100).toFixed(0)}
                </span>
              )}
            </div>
          </button>
        )
      })}
      </div>
    </div>
  )
}

function SizeImageOrFallback({
  imageUrl,
  label,
  value,
}: {
  imageUrl: string | null
  label: string
  value: string
}) {
  const [failed, setFailed] = useState(false)
  if (imageUrl && !failed) {
    // Force a uniform render box: width:100%, aspect-ratio 1/1,
    // object-fit: cover. The merchant-uploaded webp files have
    // inconsistent intrinsic dimensions (Small Slingshot is short +
    // wide; Medium/Large sedans are tall + narrow), so object-fit:
    // contain left each silhouette occupying a different portion of
    // its tile — caught 2026-06-03 when Justin reported "size images
    // when building the kit are different sizes". cover guarantees
    // every tile fills identically; the source crops handle scale.
    return (
      <img
        src={imageUrl}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          // Image sits at 75% of the tile height (was filling 100% and
          // overflowing the white container). `contain` shows the whole
          // silhouette, centered with breathing room top + bottom.
          width: "100%",
          height: "75%",
          objectFit: "contain",
          objectPosition: "center",
          display: "block",
        }}
      />
    )
  }
  // Fallback for unknown sizes or CDN failures — generic car silhouette.
  const Silhouette = silhouetteFor(label, value)
  return <Silhouette />
}

// Pick a silhouette per chip label/value. Falls back to sedan when the
// label doesn't match a known size — better to render a generic car than
// nothing at all if the merchant adds a new size variant.
function silhouetteFor(label: string, value: string): () => ReactElement {
  const key = `${label} ${value}`.toLowerCase()
  if (key.includes("small") || key.includes("compact")) return CompactSilhouette
  if (key.includes("extra") || key.includes("xl") || key.includes("oversize") || key.includes("truck"))
    return TruckSilhouette
  if (key.includes("large") || key.includes("suv")) return SUVSilhouette
  return SedanSilhouette
}

// All silhouettes share a 200x80 viewBox and currentColor fill so they
// scale uniformly and inherit the parent tile's text color. The relative
// width on the wrapper SVG controls perceived vehicle size — smaller
// vehicles render narrower so the comparison reads at a glance.

function CompactSilhouette() {
  return (
    <svg
      viewBox="0 0 200 80"
      style={{ width: "55%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M40 56 Q42 38 60 36 L80 24 Q92 18 110 18 L135 18 Q150 18 158 30 L172 36 Q182 38 184 50 L184 60 Q184 64 178 64 L170 64 A12 12 0 0 1 146 64 L82 64 A12 12 0 0 1 58 64 L46 64 Q40 64 40 60 Z"
      />
      <circle cx="70" cy="64" r="9" fill="#0d152c" />
      <circle cx="70" cy="64" r="4" fill="#6dc3f7" />
      <circle cx="158" cy="64" r="9" fill="#0d152c" />
      <circle cx="158" cy="64" r="4" fill="#6dc3f7" />
    </svg>
  )
}

function SedanSilhouette() {
  return (
    <svg
      viewBox="0 0 200 80"
      style={{ width: "75%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M16 56 Q18 40 36 36 L58 22 Q72 14 96 14 L140 14 Q160 14 170 26 L186 34 Q196 38 198 50 L198 62 Q198 66 192 66 L182 66 A14 14 0 0 1 154 66 L70 66 A14 14 0 0 1 42 66 L24 66 Q16 66 16 62 Z"
      />
      <circle cx="56" cy="66" r="11" fill="#0d152c" />
      <circle cx="56" cy="66" r="5" fill="#6dc3f7" />
      <circle cx="168" cy="66" r="11" fill="#0d152c" />
      <circle cx="168" cy="66" r="5" fill="#6dc3f7" />
    </svg>
  )
}

function SUVSilhouette() {
  return (
    <svg
      viewBox="0 0 200 80"
      style={{ width: "85%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M14 58 Q16 36 34 32 L46 16 Q58 8 84 8 L150 8 Q172 8 180 22 L188 32 Q198 36 198 50 L198 62 Q198 66 192 66 L180 66 A14 14 0 0 1 152 66 L66 66 A14 14 0 0 1 38 66 L20 66 Q14 66 14 62 Z"
      />
      <circle cx="52" cy="66" r="12" fill="#0d152c" />
      <circle cx="52" cy="66" r="5" fill="#6dc3f7" />
      <circle cx="166" cy="66" r="12" fill="#0d152c" />
      <circle cx="166" cy="66" r="5" fill="#6dc3f7" />
    </svg>
  )
}

function TruckSilhouette() {
  return (
    <svg
      viewBox="0 0 200 80"
      style={{ width: "95%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M8 60 L8 44 Q8 32 22 30 L34 14 Q44 8 70 8 L106 8 Q118 8 122 18 L126 30 L186 30 Q196 32 196 44 L196 62 Q196 66 190 66 L180 66 A14 14 0 0 1 152 66 L66 66 A14 14 0 0 1 38 66 L18 66 Q8 66 8 60 Z"
      />
      <circle cx="52" cy="66" r="12" fill="#0d152c" />
      <circle cx="52" cy="66" r="5" fill="#6dc3f7" />
      <circle cx="166" cy="66" r="12" fill="#0d152c" />
      <circle cx="166" cy="66" r="5" fill="#6dc3f7" />
    </svg>
  )
}
